import { Controller } from "@hotwired/stimulus"
import { replaceOptions, setDisabled } from "../shared/multi_select"

export default class extends Controller {
  static targets = ["heading", "content", "department", "departmentError", "blacklistModal", "blacklistFrame", "pageSizeTemplate"]

  static values = {
    url: String,
    htmlUrl: String,
    csvUrl: String,
    errorMessage: String,
    departmentsUrl: String,
    departmentsErrorMessage: String
  }

  connect() {
    this.currentPage = 1
    this.pageSize = 25
    this.abortController = new AbortController()
    this.observeLayoutHeight()
    this.refreshAfterBlacklist = () => {
      this.currentPage = 1
      this.load()
      this.blacklistFrameTarget.removeAttribute("src")
    }
    this.blacklistModalTarget.addEventListener("hidden.coreui.modal", this.refreshAfterBlacklist)
    this.load()
  }

  disconnect() {
    this.abortController.abort()
    this.departmentsAbortController?.abort()
    this.layoutObserver.disconnect()
    this.blacklistModalTarget.removeEventListener("hidden.coreui.modal", this.refreshAfterBlacklist)
  }

  changeCompany(event) {
    this.refreshDepartments(event.currentTarget.form)
  }

  changeDates(event) {
    const form = event.currentTarget.form
    if (!["start_date", "end_date"].every((name) => form.elements.namedItem(name).validity.valid)) return

    this.refreshDepartments(form, true)
  }

  async refreshDepartments(form, preserveSelection = false) {
    const previous = preserveSelection
      ? this.pendingDepartmentCodes || Array.from(this.departmentTarget.selectedOptions, (option) => option.value)
      : []
    this.pendingDepartmentCodes = previous
    this.departmentsAbortController?.abort()
    const request = new AbortController()
    this.departmentsAbortController = request
    this.departmentErrorTarget.hidden = true
    this.setDepartmentTree([])
    setDisabled(this.departmentTarget, true)
    replaceOptions(this.departmentTarget, [])

    const company = form.elements.namedItem("company_code")
    if (!company.value) return

    const url = new URL(this.departmentsUrlValue, window.location.origin)
    url.searchParams.set("company_code", company.value)
    for (const name of ["start_date", "end_date"]) {
      url.searchParams.set(name, form.elements.namedItem(name).value)
    }
    previous.forEach((code) => url.searchParams.append("department_codes[]", code))

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: request.signal
      })
      const payload = await response.json()
      if (request.signal.aborted) return
      if (!response.ok) throw new Error(payload.error || this.departmentsErrorMessageValue)

      this.setDepartmentTree(payload.tree)
      replaceOptions(this.departmentTarget, payload.departments)
      setDisabled(this.departmentTarget, payload.departments.length === 0)
    } catch (error) {
      if (request.signal.aborted) return

      this.departmentErrorTarget.textContent = this.departmentsErrorMessageValue
      this.departmentErrorTarget.hidden = false
    } finally {
      if (!request.signal.aborted) this.pendingDepartmentCodes = null
    }
  }

  setDepartmentTree(tree) {
    const config = JSON.parse(this.departmentTarget.dataset.coreuiConfig)
    config.tree = tree
    this.departmentTarget.dataset.coreuiConfig = JSON.stringify(config)
  }

  openBlacklist(event) {
    const form = event.currentTarget.form
    if (!form.reportValidity()) return

    const url = new URL(event.currentTarget.dataset.url, window.location.origin)
    url.search = new URLSearchParams(new FormData(form)).toString()
    url.searchParams.set("in_iframe", "1")
    this.blacklistFrameTarget.src = url.toString()
    this.blacklistModalTarget.setAttribute("data-coreui-modal-open-value", "true")
  }

  observeLayoutHeight() {
    const layout = this.element.closest(".wrapper")
    const chrome = Array.from(layout?.querySelectorAll(":scope > .header, :scope > .footer") || [])
    const updateHeight = () => {
      const height = chrome.reduce((total, element) => total + element.getBoundingClientRect().height, 0)
      this.element.style.setProperty("--working-hours-layout-height", `${height}px`)
    }

    this.layoutObserver = new ResizeObserver(updateHeight)
    chrome.forEach((element) => this.layoutObserver.observe(element))
    updateHeight()
  }

  async load() {
    try {
      const response = await fetch(this.urlWithCurrentQuery(this.urlValue), {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })
      const payload = await response.json()

      if (!response.ok) throw new Error(payload.error || this.errorMessageValue)

      this.payload = payload
      this.renderReport()
    } catch (error) {
      if (error.name !== "AbortError") this.renderError(error.message || this.errorMessageValue)
    }
  }

  renderReport() {
    this.headingTarget.replaceChildren(this.buildHeading())
    this.contentTarget.replaceChildren(this.buildReportCard())
  }

  renderError(message) {
    const card = this.elementFor("div", "card shadow-sm")
    const body = this.elementFor("div", "card-body")
    const alert = this.elementFor("div", "alert alert-danger mb-0", message)
    alert.setAttribute("role", "alert")
    body.append(alert)
    card.append(body)
    this.contentTarget.replaceChildren(card)
  }

  buildHeading() {
    const wrapper = this.elementFor("div", "d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3")
    const copy = document.createElement("div")
    const pageTitle = document.querySelector("[data-portal-page-title]")
    if (pageTitle) pageTitle.textContent = this.payload.meta.title
    const subtitle = this.elementFor("p", "text-body-secondary mb-0", this.payload.meta.subtitle)
    copy.append(subtitle)
    wrapper.append(copy)

    if (this.payload.meta.refresh_time) {
      const badge = this.elementFor("span", "badge bg-info-subtle text-info-emphasis")
      const icon = this.elementFor("i", "fa-regular fa-arrows-rotate me-1")
      icon.setAttribute("aria-hidden", "true")
      badge.append(icon, document.createTextNode(this.payload.meta.refresh_time))
      wrapper.append(badge)
    }

    return wrapper
  }

  buildReportCard() {
    const card = this.elementFor("div", "card shadow-sm working-hours-report-card")
    card.append(this.buildReportCardHeader())

    if (this.payload.filters.companies.length === 0 || this.payload.filters.departments.length === 0) {
      card.append(this.buildMessage(this.payload.labels.no_authorized_data, "alert alert-warning mb-0"))
    } else if (this.payload.rows.length === 0) {
      card.append(this.buildEmptyMessage())
    } else {
      card.append(this.buildTable(), this.buildPagination())
      this.renderRows()
    }

    return card
  }

  buildReportCardHeader() {
    const header = this.elementFor("div", "card-header d-flex flex-wrap align-items-center justify-content-between gap-2")
    const nav = this.elementFor("ul", "nav nav-pills card-header-pills")
    nav.setAttribute("aria-label", this.payload.labels.dimensions_label)

    Object.entries(this.payload.labels.dimensions).forEach(([dimension, label]) => {
      const item = this.elementFor("li", "nav-item")
      const active = this.payload.meta.dimension === dimension
      const link = this.elementFor("a", `nav-link${active ? " active" : ""}`, label)
      link.href = this.urlWithCurrentQuery(this.htmlUrlValue, {dimension})
      if (active) link.setAttribute("aria-current", "page")
      item.append(link)
      nav.append(item)
    })

    const exportLink = this.elementFor("a", "btn btn-outline-primary btn-sm")
    exportLink.href = this.urlWithCurrentQuery(this.csvUrlValue)
    const icon = this.elementFor("i", "fa-regular fa-file-arrow-down me-1")
    icon.setAttribute("aria-hidden", "true")
    exportLink.append(icon, document.createTextNode(this.payload.labels.actions.export))
    header.append(nav, exportLink)
    return header
  }

  buildTable() {
    this.tableWrapper = this.elementFor("div", "working-hours-table-wrapper")
    const table = this.elementFor("table", "table table-bordered table-hover align-middle mb-0 working-hours-table")
    const head = document.createElement("thead")
    const headingRow = document.createElement("tr")

    headingRow.append(
      this.buildHeadingCell(this.payload.labels.table.sequence, "working-hours-sticky working-hours-sequence text-center"),
      this.buildHeadingCell(this.payload.labels.table.code, "working-hours-sticky working-hours-code text-center"),
      this.buildHeadingCell(this.payload.labels.table.name, "working-hours-sticky working-hours-name text-center"),
      this.buildHeadingCell(this.payload.labels.table.audit_status, "working-hours-sticky working-hours-audit text-center")
    )

    this.payload.days.forEach((day) => {
      const className = `working-hours-day text-center${day.workday ? "" : " working-hours-nonwork"}`
      const cell = this.elementFor("th", className)
      const date = this.elementFor("span", "d-block text-nowrap", day.date)
      const weekday = this.elementFor("small", "fw-normal", day.weekday)
      cell.append(date, weekday)
      headingRow.append(cell)
    })
    headingRow.append(this.buildHeadingCell(this.payload.labels.table.total, "working-hours-total text-center"))

    head.append(headingRow)
    this.tableBody = document.createElement("tbody")
    table.append(head, this.tableBody)
    this.tableWrapper.append(table)
    return this.tableWrapper
  }

  buildPagination() {
    const footer = this.elementFor("div", "card-footer d-flex flex-wrap align-items-center justify-content-between gap-2")
    const pageSizeGroup = this.elementFor("div", "d-flex align-items-center gap-2")
    const label = this.elementFor("label", "mb-0", this.payload.labels.pagination.rows_per_page)
    const picker = this.pageSizeTemplateTarget.content.cloneNode(true)
    const select = picker.querySelector("select")
    select.value = this.pageSize
    select.addEventListener("change", (event) => {
      this.pageSize = Number(event.target.value)
      this.currentPage = 1
      this.renderRows()
    })
    label.htmlFor = "working-hours-page-size"
    select.id = "working-hours-page-size"
    pageSizeGroup.append(label, picker)

    this.paginationSummary = this.elementFor("div", "text-body-secondary")
    const buttons = this.elementFor("div", "btn-group btn-group-sm")
    this.previousButton = this.elementFor("button", "btn btn-outline-primary", this.payload.labels.pagination.previous)
    this.nextButton = this.elementFor("button", "btn btn-outline-primary", this.payload.labels.pagination.next)
    this.previousButton.type = "button"
    this.nextButton.type = "button"
    this.previousButton.addEventListener("click", () => this.changePage(-1))
    this.nextButton.addEventListener("click", () => this.changePage(1))
    buttons.append(this.previousButton, this.nextButton)
    footer.append(pageSizeGroup, this.paginationSummary, buttons)
    return footer
  }

  renderRows() {
    const startIndex = (this.currentPage - 1) * this.pageSize
    const visibleRows = this.payload.rows.slice(startIndex, startIndex + this.pageSize)
    const fragment = document.createDocumentFragment()

    visibleRows.forEach((row, localIndex) => {
      this.payload.meta.row_kinds.forEach((kind, kindIndex) => {
        const tableRow = document.createElement("tr")

        if (kindIndex === 0) {
          tableRow.classList.add("working-hours-group-start")
          tableRow.append(
            this.buildRowspanCell(startIndex + localIndex + 1, "working-hours-sticky working-hours-sequence text-center"),
            this.buildRowspanCell(row.code, "working-hours-sticky working-hours-code text-center text-nowrap"),
            this.buildRowspanCell(row.name, "working-hours-sticky working-hours-name")
          )
        }

        const audit = this.elementFor("th", "working-hours-sticky working-hours-audit text-nowrap", this.payload.labels.table.row_kinds[kind])
        audit.scope = "row"
        tableRow.append(audit)

        this.payload.days.forEach((day) => {
          tableRow.append(this.buildDataCell(row.entries[kind][day.date], day.workday))
        })

        const total = this.elementFor("td", "working-hours-total text-center fw-semibold text-nowrap", `${this.formatHours(row.totals[kind])}h`)
        tableRow.append(total)
        fragment.append(tableRow)
      })
    })

    this.tableBody.replaceChildren(fragment)
    this.updatePagination(startIndex, visibleRows.length)
    this.tableWrapper.scrollTop = 0
  }

  buildDataCell(value, workday) {
    const className = `working-hours-day${workday ? "" : " working-hours-nonwork"}`
    const cell = this.elementFor("td", className)

    if (value === undefined || value === null) {
      cell.append(this.elementFor("span", "text-body-secondary", "—"))
    } else if (this.payload.meta.daily_totals) {
      cell.append(this.elementFor("span", "fw-semibold", `${this.formatHours(value)}h`))
    } else {
      value.forEach(([label, hours]) => {
        const entry = this.elementFor("div", "working-hours-entry")
        entry.append(
          this.elementFor("span", "working-hours-entry-label", label),
          this.elementFor("span", "text-body-secondary", `${this.formatHours(hours)}h`)
        )
        entry.firstChild.append(document.createTextNode(":"))
        cell.append(entry)
      })
    }

    return cell
  }

  updatePagination(startIndex, visibleCount) {
    const total = this.payload.rows.length
    const totalPages = Math.ceil(total / this.pageSize)
    this.currentPage = Math.min(this.currentPage, totalPages)
    this.paginationSummary.textContent = this.interpolate(this.payload.labels.pagination.summary, {
      start: startIndex + 1,
      end: startIndex + visibleCount,
      total
    })
    this.previousButton.disabled = this.currentPage <= 1
    this.nextButton.disabled = this.currentPage >= totalPages
  }

  changePage(offset) {
    const totalPages = Math.ceil(this.payload.rows.length / this.pageSize)
    const nextPage = Math.max(1, Math.min(this.currentPage + offset, totalPages))
    if (nextPage === this.currentPage) return

    this.currentPage = nextPage
    this.renderRows()
  }

  buildHeadingCell(text, className) {
    return this.elementFor("th", className, text)
  }

  buildRowspanCell(text, className) {
    const cell = this.elementFor("td", className, text)
    cell.rowSpan = this.payload.meta.row_kinds.length
    return cell
  }

  buildMessage(message, alertClass) {
    const body = this.elementFor("div", "card-body")
    const alert = this.elementFor("div", alertClass, message)
    alert.setAttribute("role", "alert")
    body.append(alert)
    return body
  }

  buildEmptyMessage() {
    const body = this.elementFor("div", "card-body")
    const empty = this.elementFor("div", "text-center text-body-secondary py-5")
    const icon = this.elementFor("i", "fa-regular fa-calendar-xmark fa-2x mb-3")
    icon.setAttribute("aria-hidden", "true")
    empty.append(icon, this.elementFor("p", "mb-0", this.payload.labels.no_data))
    body.append(empty)
    return body
  }

  urlWithCurrentQuery(baseUrl, overrides = {}) {
    const url = new URL(baseUrl, window.location.origin)
    url.search = window.location.search
    Object.entries(overrides).forEach(([key, value]) => url.searchParams.set(key, value))
    return `${url.pathname}${url.search}`
  }

  formatHours(value) {
    return Number(value).toFixed(1).replace(/\.0$/, "")
  }

  interpolate(template, values) {
    return template.replace(/%\{(\w+)\}/g, (_match, key) => values[key])
  }

  elementFor(tagName, className = "", text = null) {
    const element = document.createElement(tagName)
    if (className) element.className = className
    if (text !== null && text !== undefined) element.textContent = text
    return element
  }
}
